
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, DollarSign, Calendar, MessageSquare, Trophy, BarChart3, Loader2, Link as LinkIcon, Plus } from 'lucide-react';
import { EnhancedGameCreationModal } from '@/components/games/EnhancedGameCreationModal';
import PermissionGate from '@/components/rbac/PermissionGate';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { HistoricalPerformance } from '@/components/retention/HistoricalPerformance';
import { SeasonArchiveManager } from '@/components/retention/SeasonArchiveManager';
import { EntityRolloverScheduler } from '@/components/seasons/EntityRolloverScheduler';
import { QuickActionCards } from '@/components/dashboard/QuickActionCards';
import { TeamConnectionDialog } from '@/components/dashboard/TeamConnectionDialog';
import { CreateTeamDialog } from '@/components/dashboard/CreateTeamDialog';
import { supabase } from '@/integrations/supabase/client';

interface OwnedTeam {
  team_id: number;
  team_name: string;
}

interface TeamPerformance {
  team_id: number;
  team_name: string;
  wins: number;
  losses: number;
}

const TeamOwnerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthenticatedUser();
  const [ownedTeams, setOwnedTeams] = useState<OwnedTeam[]>([]);
  const [upcomingGamesCount, setUpcomingGamesCount] = useState(0);
  const [teamPerformance, setTeamPerformance] = useState<TeamPerformance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);


  const loadDashboardData = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Fetch connected teams from user profile
      const { data: profileData } = await supabase
        .from('internal_user_profiles')
        .select('connected_teams')
        .eq('user_id', user.id)
        .single();

      const connectedTeamIds = (profileData?.connected_teams as number[]) || [];

      let teams: OwnedTeam[] = [];
      if (connectedTeamIds.length > 0) {
        const { data: teamsData } = await supabase
          .from('teams')
          .select('team_id, team_name')
          .in('team_id', connectedTeamIds);
        teams = teamsData || [];
      }

      setOwnedTeams(teams);

      if (teams.length === 0) {
        setIsLoading(false);
        return;
      }

      const teamIds = teams.map(t => t.team_id);

      // Fetch upcoming games for owned teams (this week)
      const now = new Date();
      const endOfWeek = new Date(now);
      endOfWeek.setDate(now.getDate() + 7);

      const { data: upcomingGames } = await supabase
        .from('venue_games')
        .select('id')
        .or(`home_team_id.in.(${teamIds.join(',')}),away_team_id.in.(${teamIds.join(',')})`)
        .gte('game_date', now.toISOString().split('T')[0])
        .lte('game_date', endOfWeek.toISOString().split('T')[0])
        .neq('game_status', 'completed')
        .neq('game_status', 'cancelled');

      setUpcomingGamesCount(upcomingGames?.length || 0);

      // Fetch team performance (W/L records) from game_results
      const performanceData: TeamPerformance[] = [];
      
      for (const team of teams) {
        // Get completed games where this team was home team, joined with results
        const { data: homeGames } = await supabase
          .from('venue_games')
          .select('id, home_team_id, away_team_id, game_results(home_team_score, away_team_score)')
          .eq('home_team_id', team.team_id)
          .eq('game_status', 'completed');

        const { data: awayGames } = await supabase
          .from('venue_games')
          .select('id, home_team_id, away_team_id, game_results(home_team_score, away_team_score)')
          .eq('away_team_id', team.team_id)
          .eq('game_status', 'completed');

        let wins = 0;
        let losses = 0;

        // Calculate wins/losses from home games
        (homeGames || []).forEach(game => {
          const results = game.game_results;
          if (Array.isArray(results) && results.length > 0) {
            const result = results[0];
            if (result.home_team_score !== null && result.away_team_score !== null) {
              if (result.home_team_score > result.away_team_score) wins++;
              else if (result.home_team_score < result.away_team_score) losses++;
            }
          }
        });

        // Calculate wins/losses from away games
        (awayGames || []).forEach(game => {
          const results = game.game_results;
          if (Array.isArray(results) && results.length > 0) {
            const result = results[0];
            if (result.home_team_score !== null && result.away_team_score !== null) {
              if (result.away_team_score > result.home_team_score) wins++;
              else if (result.away_team_score < result.home_team_score) losses++;
            }
          }
        });

        performanceData.push({
          team_id: team.team_id,
          team_name: team.team_name,
          wins,
          losses
        });
      }

      setTeamPerformance(performanceData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [user?.id]);

  const handleTeamConnected = () => {
    loadDashboardData();
  };

  const getWinLossColor = (wins: number, losses: number) => {
    if (wins === 0 && losses === 0) return 'text-gray-400';
    const ratio = wins / (wins + losses);
    if (ratio >= 0.6) return 'text-sports-green';
    if (ratio >= 0.4) return 'text-yellow-400';
    return 'text-red-400';
  };

  const teamCount = ownedTeams.length;

  // If no teams, show onboarding prompt
  if (!isLoading && teamCount === 0) {
    return (
      <div className="space-y-6">
        {/* Team Onboarding Card */}
        <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700">
          <CardContent className="p-8">
            <div className="text-center max-w-lg mx-auto">
              <div className="w-16 h-16 bg-sports-green/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-sports-green" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Connect or Create Your Team</h2>
              <p className="text-gray-400 mb-6">
                To get started as a Team Owner, you need to connect to an existing team or create a new one.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => setShowConnectDialog(true)}
                  variant="outline"
                  className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Connect Existing Team
                </Button>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-sports-green text-black hover:bg-green-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Team
                </Button>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-700">
                <p className="text-gray-500 text-sm">
                  <strong className="text-gray-400">Team Not Listed?</strong>
                  <br />
                  If your team isn't in our system yet, you can create it and manage it from here.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dialogs */}
        <TeamConnectionDialog
          open={showConnectDialog}
          onOpenChange={setShowConnectDialog}
          userId={user?.id || ''}
          onTeamConnected={handleTeamConnected}
        />
        <CreateTeamDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          userId={user?.id || ''}
          onTeamCreated={handleTeamConnected}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dialogs */}
      <TeamConnectionDialog
        open={showConnectDialog}
        onOpenChange={setShowConnectDialog}
        userId={user?.id || ''}
        onTeamConnected={handleTeamConnected}
      />
      <CreateTeamDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        userId={user?.id || ''}
        onTeamCreated={handleTeamConnected}
      />

      {/* Quick Actions */}
      <PermissionGate permission="manage_team">
        <QuickActionCards 
          userRole="team-owner"
          onScheduleGame={() => setShowGameModal(true)}
          onManagePlayers={() => navigate('/manage-players')}
        />
      </PermissionGate>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <PermissionGate permission="manage_team">
          <Card 
            className="bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-700 hover:border-sports-green transition-all"
            onClick={() => navigate('/team-management')}
          >
            <CardContent className="p-4 flex items-center space-x-4">
              <Users className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Team Management</h4>
                <p className="text-sm text-gray-300">
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading...
                    </span>
                  ) : teamCount === 0 ? (
                    'No teams yet'
                  ) : (
                    `${teamCount} team${teamCount === 1 ? '' : 's'} owned`
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="view_analytics">
          <Card className="bg-gray-800 border-gray-700 opacity-75 cursor-not-allowed">
            <CardContent className="p-4 flex items-center space-x-4">
              <DollarSign className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Revenue</h4>
                <p className="text-sm text-gray-300">Coming soon</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="view_schedules">
          <Card 
            className="bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-700 hover:border-sports-green transition-all"
            onClick={() => navigate('/games')}
          >
            <CardContent className="p-4 flex items-center space-x-4">
              <Calendar className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Upcoming Games</h4>
                <p className="text-sm text-gray-300">
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading...
                    </span>
                  ) : upcomingGamesCount === 0 ? (
                    'No upcoming games'
                  ) : (
                    `${upcomingGamesCount} this week`
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      {/* Add Another Team Section */}
      <PermissionGate permission="manage_team">
        <Card className="bg-gradient-to-r from-gray-800 to-gray-900 border-gray-700 border-dashed">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sports-green/20 rounded-full flex items-center justify-center">
                  <Plus className="h-5 w-5 text-sports-green" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Add Another Team</h4>
                  <p className="text-sm text-gray-400">Connect to an existing team or create a new one</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowConnectDialog(true)}
                  variant="outline"
                  className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Connect Existing
                </Button>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-sports-green text-black hover:bg-green-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </PermissionGate>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PermissionGate permission="manage_team">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <Trophy className="h-5 w-5 text-sports-green" />
                Team Performance
              </h4>
              <div className="space-y-3">
                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : teamPerformance.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">No team performance data yet</p>
                ) : (
                  teamPerformance.map((team) => (
                    <div key={team.team_id} className="flex justify-between items-center p-3 bg-gray-700 rounded-md">
                      <span className="text-white">{team.team_name}</span>
                      <span className={`font-medium ${getWinLossColor(team.wins, team.losses)}`}>
                        {team.wins}-{team.losses} W/L
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="view_analytics">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-sports-green" />
                Financial Overview
              </h4>
              <p className="text-gray-400 text-center py-4">
                Financial tracking coming soon
              </p>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      <PermissionGate permission="create_chat_groups">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-sports-green" />
              Communication Hub
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-700 p-4 rounded-lg">
                <h5 className="font-medium text-white mb-2">Team Announcements</h5>
                <p className="text-sm text-gray-300">Broadcast important updates to all teams</p>
              </div>
              <div className="bg-gray-700 p-4 rounded-lg">
                <h5 className="font-medium text-white mb-2">Staff Coordination</h5>
                <p className="text-sm text-gray-300">Coordinate with coaches and support staff</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </PermissionGate>

      <PermissionGate permission="view_analytics">
        <HistoricalPerformance 
          entityType="team" 
          entityId={user?.id || ''} 
        />
      </PermissionGate>

      {/* Season Management Section */}
      <PermissionGate permission="manage_team">
        {user?.id && ownedTeams.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SeasonArchiveManager 
              entityId={ownedTeams[0].team_id.toString()} 
              entityType="team"
            />
            <EntityRolloverScheduler
              entityType="team"
              entityIds={ownedTeams.map(t => t.team_id.toString())}
              entityName="My Teams"
            />
          </div>
        )}
      </PermissionGate>
      <EnhancedGameCreationModal
        userId={user?.id || ''}
        externalOpen={showGameModal}
        onExternalOpenChange={setShowGameModal}
        onGameCreated={() => setShowGameModal(false)}
      />
    </div>
  );
};

export default TeamOwnerDashboard;
